import { gql, useMutation } from "@apollo/client";

import {
  Collection,
  SyncCollectionAssetsInput,
  UpdateCollectionInput,
  UpdateCollectionThumbnailInput,
} from "../../../generated/gql/graphql";

const DELETE_COLLECTION = gql`
  mutation DeleteCollection($deleteCollectionId: String!) {
    deleteCollection(id: $deleteCollectionId)
  }
`;

const UPDATE_COLLECTION = gql`
  mutation UpdateCollection(
    $updateCollectionId: String!
    $input: UpdateCollectionInput!
  ) {
    updateCollection(id: $updateCollectionId, input: $input) {
      id
      status
      sort
      name
      image
      userId
      sharedUserIds
      dateCreated
      dateUpdated
      createdBy
    }
  }
`;

const SYNC_COLLECTION_ASSETS = gql`
  mutation SyncCollectionAssets($input: SyncCollectionAssetsInput!) {
    syncCollectionAssets(input: $input)
  }
`;

const UPDATE_COLLECTION_THUMBNAIL = gql`
  mutation UpdateCollectionThumbnail($input: UpdateCollectionThumbnailInput!) {
    updateCollectionThumbnail(input: $input)
  }
`;

type CollectionApi = {
  updateCollection(
    id: string,
    input: UpdateCollectionInput
  ): Promise<Collection>;
  deleteCollection(id: string): Promise<boolean>;
  syncCollectionAssets(input: SyncCollectionAssetsInput): Promise<void>;
  updateCollectionThumbnail(
    input: UpdateCollectionThumbnailInput
  ): Promise<void>;
};

export default function useCollection(): CollectionApi {
  const [deleteCollectionMut] = useMutation(DELETE_COLLECTION);
  const [updateCollectionMut] =
    useMutation<UpdateCollectionInput>(UPDATE_COLLECTION);
  const [syncCollectionAssetsMut] = useMutation<SyncCollectionAssetsInput>(
    SYNC_COLLECTION_ASSETS
  );
  const [updateCollectionThumbnailMut] =
    useMutation<UpdateCollectionThumbnailInput>(UPDATE_COLLECTION_THUMBNAIL);

  async function deleteCollection(id: string): Promise<boolean> {
    if (!id) {
      throw new Error("Id is required");
    }

    try {
      await deleteCollectionMut({
        variables: {
          deleteCollectionId: id,
        },
      });

      return true;
    } catch (error) {
      throw new Error(`Error while deleting collection. ${error}`);
    }
  }

  async function updateCollection(
    id: string,
    input: UpdateCollectionInput
  ): Promise<Collection> {
    if (!id) {
      throw new Error("Id is required");
    }

    try {
      const result = await updateCollectionMut({
        variables: {
          updateCollectionId: id,
          input,
        },
      });

      if (result.errors && result.errors.length > 0) {
        return Promise.reject(
          new Error(`Error updating collection. ${result.errors}`)
        );
      }

      if (!result.data) {
        return Promise.reject(new Error("Failed to update collection"));
      }

      return result.data as Collection;
    } catch (error) {
      throw new Error(`Error while updating collection. ${error}`);
    }
  }

  async function syncCollectionAssets(
    input: SyncCollectionAssetsInput
  ): Promise<void> {
    await syncCollectionAssetsMut({
      variables: {
        input,
      },
    });
  }

  async function updateCollectionThumbnail(
    input: UpdateCollectionThumbnailInput
  ): Promise<void> {
    await updateCollectionThumbnailMut({
      variables: {
        input,
      },
    });
  }

  return {
    deleteCollection,
    updateCollection,
    syncCollectionAssets,
    updateCollectionThumbnail,
  };
}
